/**
 * TreeMapControl
 *
 * Tree map for hierarchical data visualization
 * Shows proportional area representation
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData } from './BaseChartComponent';

interface TreeNode {
  name: string;
  value: number;
  color: string;
}

const TreeMapControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);

  const treemapConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      labelKey: (config?.labelKey as string) || 'label',
      valueKey: (config?.valueKey as string) || 'value',
    };
  }, [control.config]);

  const renderTreeMap = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    // Extract nodes
    const nodes: TreeNode[] = [];
    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c',
      '#d084d0', '#13c2c2', '#f5ad14', '#eb2f96', '#52c41a',
    ];

    chartData.forEach((d, idx) => {
      const label = String((d as Record<string, unknown>)[treemapConfig.labelKey] || `Item ${idx}`);
      const val = (d as Record<string, unknown>)[treemapConfig.valueKey] as number | undefined;

      if (typeof val === 'number' && val > 0) {
        nodes.push({
          name: label,
          value: val,
          color: colors[idx % colors.length],
        });
      }
    });

    if (nodes.length === 0) return null;

    // Simple treemap layout (slice and dice algorithm)
    const totalValue = nodes.reduce((a, b) => a + b.value, 0);
    const width = 600;
    const height = (control.config?.height as number) || 400;

    interface RectLayout {
      name: string;
      value: number;
      color: string;
      x: number;
      y: number;
      w: number;
      h: number;
    }

    const rectangles: RectLayout[] = [];
    let x = 0;
    let y = 0;
    let remainingWidth = width;
    let remainingHeight = height;
    let rowWidth = width;

    nodes.forEach((node, index) => {
      const ratio = node.value / totalValue;
      const nodeWidth = remainingWidth;
      const nodeHeight = remainingHeight * ratio;

      rectangles.push({
        name: node.name,
        value: node.value,
        color: node.color,
        x,
        y,
        w: nodeWidth,
        h: nodeHeight,
      });

      y += nodeHeight;
      remainingHeight -= nodeHeight;

      if (index === nodes.length - 1 && remainingHeight > 0) {
        rectangles[rectangles.length - 1].h += remainingHeight;
      }
    });

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Rectangles */}
        {rectangles.map((rect, index) => {
          const textX = rect.x + rect.w / 2;
          const textY = rect.y + rect.h / 2;
          const percentage = ((rect.value / totalValue) * 100).toFixed(1);

          return (
            <g key={`rect-${index}`}>
              <rect
                x={rect.x}
                y={rect.y}
                width={Math.max(rect.w, 0)}
                height={Math.max(rect.h, 0)}
                fill={rect.color}
                opacity="0.8"
                stroke="white"
                strokeWidth="2"
              />
              {rect.w > 60 && rect.h > 30 && (
                <>
                  <text
                    x={textX}
                    y={textY - 8}
                    textAnchor="middle"
                    fontSize="13"
                    fontWeight="bold"
                    fill="white"
                  >
                    {rect.name.substring(0, 12)}
                  </text>
                  <text
                    x={textX}
                    y={textY + 12}
                    textAnchor="middle"
                    fontSize="12"
                    fill="white"
                  >
                    {percentage}%
                  </text>
                </>
              )}
            </g>
          );
        })}
      </svg>
    );
  }, [chartData, treemapConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="tree-map">
      <div style={{ width: '100%', height: '100%' }}>{renderTreeMap}</div>
    </BaseChartComponent>
  );
});

TreeMapControl.displayName = 'TreeMapControl';
export default TreeMapControl;
